import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';

import { environment } from '@env';

import { AuthService } from '../services/auth.service';
import { AuthStorageService } from '../services/auth-storage.service';
import { SessionService } from '../services/session.service';

const EXCLUDED_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout'];

function isRefreshable(req: HttpRequest<unknown>): boolean {
  if (!req.url.startsWith(environment.apiUrl)) return false;
  return !EXCLUDED_PATHS.some((path) => req.url.endsWith(path));
}

export const refreshInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const storage = inject(AuthStorageService);
  // Resolved lazily: SessionService depends on TranslateService, whose loader issues HTTP requests
  // through this interceptor — injecting it eagerly is a DI cycle (NG0200) and breaks i18n loading.
  const injector = inject(Injector);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401 || !isRefreshable(req)) {
        return throwError(() => err);
      }
      return from(authService.refresh()).pipe(
        switchMap(() => {
          const token = storage.accessToken();
          const retried = token
            ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` }, withCredentials: true })
            : req.clone({ withCredentials: true });
          return next(retried);
        }),
        catchError((refreshErr: unknown) => {
          // The server no longer accepts the session (idle/absolute expiry, revoked, other tab):
          // send a signed-in user to the login. Anonymous bootstrap (no session yet) stays silent.
          if (authService.isAuthenticated()) void injector.get(SessionService).end('expired');
          return throwError(() => refreshErr);
        }),
      );
    }),
  );
};
